import { Box, Heading, Section, Stack, Text } from "@vinomofo/components";
import { useEffect, useRef, useState } from "react";

import { BadgeStack } from "../badge-stack";
import { DashIcon } from "@vinomofo/icons";
import { OfferDetailsSingleProps } from "../offer-details";
import { OfferHeroCard } from "./OfferHeroCard";
import { OfferHeroImage } from "./OfferHeroImage";
import { OfferHeroImageSingleWide } from "./OfferHeroImageSingleWide";
import { OfferSellingPoints } from "./OfferSellingPoints";
import { VinoDataOffer } from "../../types/vino-data-types";

export interface OfferHeroSingleProps {
  details: OfferDetailsSingleProps;
  offer: VinoDataOffer;
}

export const OfferHeroSingle = ({
  details,
  offer,
}: OfferHeroSingleProps): JSX.Element => {
  const {
    productId,
    endsAt,
    price,
    unitRRP,
    priceDescription,
    shortDescription,
    discountPercentage,
    discountDollarValue,
    discountDisplay,
    region,
    style,
    showPriceDrop,
    showCountdown,
    awards,
    heroImage,
    relativeSizedHeroImage,
    showDiscountBadge,
    showAwardBadges,
    urgencyMessage,
    isSoldOut,
    tagLine,
    subTagLine,
    name,
    roundelsImage,
    bottleShape,
  } = details;

  const sectionRef = useRef<HTMLElement>(null);
  const heroImageRef = useRef<HTMLElement>(null);

  const [imageHeight, setImageHeight] = useState(0);

  useEffect(() => {
    if (heroImageRef.current) setImageHeight(heroImageRef.current.clientHeight);
  }, [heroImageRef?.current?.clientHeight]);

  return (
    <Section ref={sectionRef} minHeight={imageHeight + 20}>
      <Box
        display={["block", "block", "flex"]}
        justifyContent="space-between"
        alignItems="start"
      >
        <Box
          flexBasis={["100%", "100%", "39.65%"]}
          maxWidth={["100%", "66%", "460px"]}
          paddingTop={["20px", "20px", "90px"]}
          alignItems={["", "center", ""]}
          display={["", "flex", ""]}
          minHeight={imageHeight}
          position="relative"
        >
          <OfferHeroImage
            name={name}
            image={heroImage}
            roundelsImage={roundelsImage}
            discountPercent={discountPercentage}
            discountDisplay={discountDisplay}
            discountDollarValue={discountDollarValue}
            showDiscountBadge={showDiscountBadge}
            isAvailable={!isSoldOut}
            display={["block", "none", "none"]}
            marginTop={["0", "15px !important", "0"]}
            bottleShape={bottleShape}
          />
          <OfferHeroImageSingleWide
            name={name}
            image={relativeSizedHeroImage}
            roundelsImage={roundelsImage}
            discountPercent={discountPercentage}
            discountDisplay={discountDisplay}
            discountDollarValue={discountDollarValue}
            showDiscountBadge={showDiscountBadge}
            isAvailable={!isSoldOut}
            display={["none", "block", "block"]}
            marginTop={["0", "15px !important", "0"]}
            ref={heroImageRef}
            bottleShape={bottleShape}
          />
          <Stack space={[0, 2, 3]}>
            {tagLine || subTagLine ? (
              <Stack space={1}>
                {tagLine && (
                  <Heading
                    as="p"
                    fontSize={["24px", "40px", "48px"]}
                    variant="xl"
                    fontWeight="700"
                  >
                    {tagLine}
                  </Heading>
                )}
                {subTagLine && (
                  <Text variant="sm" color="text.heading" fontWeight="500">
                    <Box
                      display="inline-block"
                      color="brand"
                      verticalAlign="middle"
                      marginRight="4px"
                      as="span"
                    >
                      <DashIcon />
                    </Box>
                    {subTagLine}
                  </Text>
                )}
              </Stack>
            ) : null}

            {showAwardBadges && awards?.length > 0 ? (
              <Box
                display="block"
                position={["absolute", "relative"]}
                top="4px"
              >
                <BadgeStack
                  badges={awards}
                  limit={5}
                  horizontal={[false, true]}
                  gap={["-10px", "10px", "-20px"]}
                />
              </Box>
            ) : null}

            <Box display={["none", "block"]}>
              <OfferSellingPoints
                isSpirit={offer?.isSpirit}
                isAvailable={!isSoldOut}
              />
            </Box>
          </Stack>
          <Box display={["block", "none"]}>
            <OfferSellingPoints
              isSpirit={offer?.isSpirit}
              isAvailable={!isSoldOut}
            />
          </Box>
        </Box>
        <Box
          flexBasis={["100%", "100%", "31.034%"]}
          maxWidth={["100%", "100%", "360px"]}
          paddingTop={["0", "0", "55px"]}
          mb={[0, 40, 0]}
        >
          <OfferHeroCard
            productId={productId}
            shortDescription={shortDescription}
            price={price}
            rrp={unitRRP}
            priceDescription={priceDescription}
            region={region}
            style={style}
            endsAt={endsAt}
            showCountdown={showCountdown}
            showPriceDrop={showPriceDrop}
            urgencyMessage={urgencyMessage}
            isSoldOut={isSoldOut}
            name={name}
            offer={offer}
            hideIconVarietal={true}
          />
        </Box>
      </Box>
    </Section>
  );
};
